import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Masonry from 'react-masonry-css';
import { Character2DLayered, CharacterPassport, CharacterVoice, SavedVoiceSample, Generation } from '../types';
import { loadCharacter2D, getPassportByCharacterId, saveCharacter2D, saveCharacterPassport, updateAvatarYPosition } from '../lib/character2dService';
import { getVoiceByCharacterId, deleteCharacterVoice } from '../lib/characterVoiceService';
import { loadSession } from '../lib/characterSessionStorage';
import { generateCharacterPassport } from '../lib/characterPassportService';
import { generateId } from '../lib/utils';
import { supabase } from '../lib/supabase';
import {
  ArrowLeftIcon,
  UserIcon,
  PencilSimpleIcon,
  HeartIcon,
  WarningIcon,
  CheckIcon,
  PlayCircleIcon,
  CircleNotchIcon,
  SparkleIcon,
  CaretLeftIcon,
  CaretRightIcon,
  ArrowsOutCardinalIcon,
  ImagesIcon,
  ArrowsOutIcon,
  SpeakerHighIcon,
  ArticleIcon,
  ImageSquareIcon,
  Pencil,
} from '@phosphor-icons/react';
import { useToast } from '../context/ToastContext';
import { useAuth } from '../context/AuthContext';
import { useProjectPermissions } from '../hooks/useProjectPermissions';
import { FullscreenImageViewer, ViewerImage } from './FullscreenImageViewer';
import { InlineVoiceConfigurator, SavedVoicesPanel, VoiceHeaderSection, VoiceSettingsSection } from './voice';
import { useVoiceConfigurator } from './voice/useVoiceConfigurator';
import { CharacterProductionBar, CharacterTab, TAB_CONFIG, CHARACTER_TABS } from './CharacterProductionBar';
import { getStyleById } from '../lib/characterStyles';
import { CharacterArchetype } from '../types';
import { CreditCostBadge } from './CreditCostBadge';
import { SPECIAL_CREDIT_COSTS } from '../lib/credits';

// Helper to format archetype for display
const formatArchetype = (archetype: CharacterArchetype | undefined): string => {
  if (!archetype) return '';
  const formatMap: Record<CharacterArchetype, string> = {
    'manga_anime': 'Anime/Manga',
    'film': 'Film',
    'game_3d': '3D/Animation',
    'comic_cartoon': 'Comic/Cartoon'
  };
  return formatMap[archetype] || archetype;
};

interface CharacterProfilePageProps {
  characterId: string;
  onBack: () => void;
  onResumeSession?: (characterId: string) => void;
}

type ProfileTab = 'biography' | 'voice' | 'reference' | 'images';

export const CharacterProfilePage: React.FC<CharacterProfilePageProps> = ({
  characterId,
  onBack,
  onResumeSession
}) => {
  const { showToast } = useToast();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [character, setCharacter] = useState<Character2DLayered | null>(null);

  // Check permissions for the character's project
  const { canEdit, canViewPages } = useProjectPermissions(character?.project_id || null);
  const [passport, setPassport] = useState<CharacterPassport | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isGeneratingPassport, setIsGeneratingPassport] = useState(false);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [activeTab, setActiveTab] = useState<ProfileTab>('biography');

  // Character images from generations (images that used this character as reference)
  const [characterImages, setCharacterImages] = useState<Generation[]>([]);
  const [isLoadingImages, setIsLoadingImages] = useState(false);
  const [imagesFullscreenIndex, setImagesFullscreenIndex] = useState<number | null>(null);

  // Avatar position adjustment (0-100, where 30 is default for mid-upper face)
  const [avatarYPosition, setAvatarYPosition] = useState(30);
  const [isAdjustingAvatar, setIsAdjustingAvatar] = useState(false);

  // New fields for core desire and main flaw
  const [coreDesire, setCoreDesire] = useState('');
  const [mainFlaw, setMainFlaw] = useState('');
  const [isEditingDesire, setIsEditingDesire] = useState(false);
  const [isEditingFlaw, setIsEditingFlaw] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Character name editing state
  const [isEditingName, setIsEditingName] = useState(false);
  const [editingName, setEditingName] = useState('');

  // Fullscreen image viewer state
  const [fullscreenImageIndex, setFullscreenImageIndex] = useState<number | null>(null);

  // Voice state
  const [characterVoice, setCharacterVoice] = useState<CharacterVoice | null>(null);
  const [selectedSavedSample, setSelectedSavedSample] = useState<SavedVoiceSample | null>(null);
  const [savedSamplesRefreshTrigger, setSavedSamplesRefreshTrigger] = useState(0);

  // Sidebar state for bottom bar positioning
  const [sidebarState, setSidebarState] = useState(() => {
    const isSidebarOpen = localStorage.getItem('ezart_sidebar_open') !== 'false';
    const isRightPanelOpen = localStorage.getItem('ezart_rightpanel_open') === 'true';
    const navPosition = localStorage.getItem('ezart_nav_position') === 'right' ? 'right' : 'left';
    return { isSidebarOpen, isRightPanelOpen, navPosition };
  });

  // Listen for sidebar state changes
  useEffect(() => {
    const handleStorageChange = () => {
      setSidebarState({
        isSidebarOpen: localStorage.getItem('ezart_sidebar_open') !== 'false',
        isRightPanelOpen: localStorage.getItem('ezart_rightpanel_open') === 'true',
        navPosition: localStorage.getItem('ezart_nav_position') === 'right' ? 'right' : 'left'
      });
    };
    window.addEventListener('storage', handleStorageChange);
    // Also poll for changes (same-tab localStorage changes don't trigger storage event)
    const interval = setInterval(handleStorageChange, 500);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
    };
  }, []);

  // Build gallery images
  const galleryImages = useMemo(() => {
    const images: { url: string; webUrl: string; label: string; type: 'reference' | 'body' | 'head' }[] = [];
    if (passport?.reference_sheet?.front_url) {
      images.push({ url: passport.reference_sheet.front_url, webUrl: passport.reference_sheet.front_url, label: 'Reference Sheet', type: 'reference' });
    }
    if (character?.core_layers.body) {
      images.push({
        url: character.core_layers.body.rendered_url,
        webUrl: character.core_layers.body.web_url || character.core_layers.body.rendered_url,
        label: 'Body',
        type: 'body'
      });
    }
    if (character?.core_layers.head) {
      images.push({
        url: character.core_layers.head.rendered_url,
        webUrl: character.core_layers.head.web_url || character.core_layers.head.rendered_url,
        label: 'Head',
        type: 'head'
      });
    }
    return images;
  }, [passport?.reference_sheet?.front_url, character?.core_layers.body, character?.core_layers.head]);

  // Initializing Voice Configurator State (Must be unconditional for React Hook Rules)
  const voiceConfigState = useVoiceConfigurator({
    characterId,
    userId: user?.id || '',
    projectId: character?.project_id || '',
    characterName: character?.name,
    characterDescription: character?.description,
    existingVoice: characterVoice,
    onVoiceSaved: (voice) => setCharacterVoice(voice),
    onVoiceDeleted: async () => {
      if (characterVoice) {
        try {
          await deleteCharacterVoice(characterVoice.id);
          setCharacterVoice(null);
          showToast('Voice removed', 'success');
        } catch (err) {
          console.error('Failed to delete voice:', err);
          showToast('Failed to remove voice', 'error');
        }
      }
    },
    headImageUrl: character?.core_layers?.head?.rendered_url,
    bodyImageUrl: character?.core_layers?.body?.rendered_url,
    referenceSheetUrl: passport?.reference_sheet?.front_url,
    archetype: character?.archetype,
    selectedSavedSample: selectedSavedSample,
    onSampleSaved: () => setSavedSamplesRefreshTrigger(prev => prev + 1),
    onClearSelection: () => setSelectedSavedSample(null),
  });

  // Load character data effect
  useEffect(() => {
    const loadCharacterData = async () => {
      try {
        const char = await loadCharacter2D(characterId);
        const session = loadSession(characterId);
        let finalCharacter = char;

        if (session && session.lastSaved > (char.updated_at || 0)) {
          const mergedCoreLayers = { ...char.core_layers };
          if (session.layerStates[1]?.currentUrl && !mergedCoreLayers.body) {
            mergedCoreLayers.body = {
              id: `${characterId}_body`,
              type: 'body',
              step: 1,
              source: 'generated',
              source_url: session.layerStates[1].currentUrl,
              rendered_url: session.layerStates[1].committedUrl || session.layerStates[1].currentUrl,
              edits: [],
              created_at: session.layerStates[1].lastUpdated,
              modified_at: session.layerStates[1].lastUpdated,
            };
          }
          if (session.layerStates[2]?.currentUrl && !mergedCoreLayers.face) {
            mergedCoreLayers.face = {
              id: `${characterId}_face`,
              type: 'face',
              step: 2,
              source: 'generated',
              source_url: session.layerStates[2].currentUrl,
              rendered_url: session.layerStates[2].committedUrl || session.layerStates[2].currentUrl,
              edits: [],
              created_at: session.layerStates[2].lastUpdated,
              modified_at: session.layerStates[2].lastUpdated,
            };
          }
          if (session.layerStates[3]?.currentUrl && !mergedCoreLayers.hair) {
            mergedCoreLayers.hair = {
              id: `${characterId}_hair`,
              type: 'hair',
              step: 3,
              source: 'generated',
              source_url: session.layerStates[3].currentUrl,
              rendered_url: session.layerStates[3].committedUrl || session.layerStates[3].currentUrl,
              edits: [],
              created_at: session.layerStates[3].lastUpdated,
              modified_at: session.layerStates[3].lastUpdated,
            };
          }
          finalCharacter = { ...char, core_layers: mergedCoreLayers };
        }

        setCharacter(finalCharacter);
        console.log('📋 CharacterProfilePage loaded character:', {
          id: finalCharacter.id,
          name: finalCharacter.name,
          hasBody: !!finalCharacter.core_layers?.body,
          hasHead: !!finalCharacter.core_layers?.head,
          bodyUrl: finalCharacter.core_layers?.body?.rendered_url,
          headUrl: finalCharacter.core_layers?.head?.rendered_url,
          core_layers: finalCharacter.core_layers,
        });
        setCoreDesire((finalCharacter as any).core_desire || '');
        setMainFlaw((finalCharacter as any).main_flaw || '');
        setAvatarYPosition((finalCharacter as any).avatar_y_position ?? 30);

        try {
          const pass = await getPassportByCharacterId(characterId);
          setPassport(pass);
        } catch (err) {
          console.warn('No passport found for character');
        }

        // Load voice configuration
        try {
          const voice = await getVoiceByCharacterId(characterId);
          setCharacterVoice(voice);
        } catch (err) {
          console.warn('No voice found for character');
        }
      } catch (err) {
        console.error('Failed to load character:', err);
      } finally {
        setIsLoading(false);
      }
    };
    loadCharacterData();
  }, [characterId]);

  // Prepare images for fullscreen viewer (character images tab)
  // This must be declared before any early returns to satisfy React's rules of hooks
  const characterViewerImages: ViewerImage[] = useMemo(() =>
    characterImages.map(gen => ({
      url: gen.image_url || gen.img || '',
      label: gen.prompt?.substring(0, 50) + (gen.prompt && gen.prompt.length > 50 ? '...' : ''),
      id: gen.id,
      metadata: { model: gen.model, settings: gen.settings }
    })),
    [characterImages]
  );

  // Load character images (generations that used this character as reference)
  useEffect(() => {
    const loadCharacterImages = async () => {
      if (!characterId || !user || !character) return;
      setIsLoadingImages(true);
      try {
        // For project characters: get all member user_ids
        // For personal characters: only use current user's id
        let allowedUserIds: string[] = [user.id];

        if (character.project_id) {
          // Get project owner
          const { data: projectData } = await supabase
            .from('projects')
            .select('user_id')
            .eq('id', character.project_id)
            .single();

          // Get all accepted project members
          const { data: membersData } = await supabase
            .from('project_members')
            .select('user_id')
            .eq('project_id', character.project_id)
            .eq('status', 'accepted');

          // Combine owner and members
          allowedUserIds = [
            projectData?.user_id,
            ...(membersData?.map(m => m.user_id) || [])
          ].filter((id): id is string => !!id);

          console.log(`📸 Project character - searching generations from ${allowedUserIds.length} users`);
        }

        // Query generations where settings->characterReference->id matches this character
        // and user_id is in the allowed list
        // Use ->> for the final key to get text instead of JSON
        const { data, error } = await supabase
          .from('generations')
          .select('*')
          .filter('settings->characterReference->>id', 'eq', characterId)
          .in('user_id', allowedUserIds)
          .order('created_at', { ascending: false })
          .limit(100);

        if (error) {
          console.error('Error fetching character images:', error);
        } else {
          console.log(`📸 Found ${data?.length || 0} generations for character ${characterId} (${character.project_id ? 'collaborative' : 'personal'})`);
          setCharacterImages(data || []);
        }
      } catch (err) {
        console.error('Failed to load character images:', err);
      } finally {
        setIsLoadingImages(false);
      }
    };
    loadCharacterImages();
  }, [characterId, user, character]);

  const handleSaveAvatarPosition = async () => {
    if (!character) return;
    setIsSaving(true);
    const success = await updateAvatarYPosition(character.id, avatarYPosition);
    if (success) {
      setCharacter({ ...character, avatar_y_position: avatarYPosition } as any);
      setIsAdjustingAvatar(false);
      showToast('Avatar position saved', 'success');
    } else {
      showToast('Failed to save', 'error');
    }
    setIsSaving(false);
  };

  const handleSaveCoreDesire = async () => {
    if (!character) return;
    setIsSaving(true);
    try {
      const updated = { ...character, core_desire: coreDesire } as any;
      await saveCharacter2D(updated);
      setCharacter(updated);
      setIsEditingDesire(false);
      showToast('Core desire saved', 'success');
    } catch (err) {
      showToast('Failed to save', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveMainFlaw = async () => {
    if (!character) return;
    setIsSaving(true);
    try {
      const updated = { ...character, main_flaw: mainFlaw } as any;
      await saveCharacter2D(updated);
      setCharacter(updated);
      setIsEditingFlaw(false);
      showToast('Main flaw saved', 'success');
    } catch (err) {
      showToast('Failed to save', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleStartEditName = () => {
    if (!character) return;
    setIsEditingName(true);
    setEditingName(character.name || '');
  };

  const handleSaveCharacterName = async () => {
    if (!character || !editingName.trim()) {
      showToast('Character name cannot be empty', 'error');
      return;
    }

    setIsSaving(true);
    try {
      const updatedCharacter = { ...character, name: editingName.trim() };
      await saveCharacter2D(updatedCharacter);
      setCharacter(updatedCharacter);
      setIsEditingName(false);
      showToast('Character name updated', 'success');
    } catch (error) {
      console.error('Failed to update character name:', error);
      showToast('Failed to update character name', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelEditName = () => {
    setIsEditingName(false);
    setEditingName('');
  };

  const handleGeneratePassport = async () => {
    if (!character || !user) return;
    setIsGeneratingPassport(true);
    try {
      showToast('Generating passport...', 'info');
      const passportResult = await generateCharacterPassport(character);
      const passportId = generateId();
      const newPassport: CharacterPassport = {
        id: passportId,
        character_id: character.id,
        user_id: user.id,
        reference_sheet: {
          front_url: passportResult.referenceSheetUrl,
          back_url: '',
          closeup_url: '',
          turnaround_url: passportResult.referenceSheetUrl
        },
        text_description: passportResult.textData.full_description,
        asset_manifest: {
          body: character.core_layers.body ? {
            asset_id: character.core_layers.body.id,
            description: character.core_layers.body.prompt_used || 'Body layer'
          } : undefined,
          head: character.core_layers.head ? {
            asset_id: character.core_layers.head.id,
            description: character.core_layers.head.prompt_used || 'Head layer'
          } : undefined
        },
        usage_prompt: passportResult.usage_prompt,
        character_data: character,
        created_at: Date.now(),
        updated_at: Date.now()
      };

      await saveCharacterPassport(newPassport);
      const charWithPassport = {
        ...character,
        passport_id: passportId,
        passport_generated_at: Date.now(),
        is_finalized: true
      };
      await saveCharacter2D(charWithPassport);
      setCharacter(charWithPassport);
      setPassport(newPassport);
      showToast('Passport generated!', 'success');
    } catch (err) {
      console.error('Failed to generate passport:', err);
      showToast('Failed to generate passport', 'error');
    } finally {
      setIsGeneratingPassport(false);
    }
  };

  const handleVoiceSaved = (voice: CharacterVoice) => {
    setCharacterVoice(voice);
  };

  const handleVoiceDelete = async () => {
    if (!characterVoice) return;
    try {
      await deleteCharacterVoice(characterVoice.id);
      setCharacterVoice(null);
      showToast('Voice removed', 'success');
    } catch (err) {
      console.error('Failed to delete voice:', err);
      showToast('Failed to remove voice', 'error');
    }
  };

  if (isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center h-screen bg-zinc-950">
        <div className="w-8 h-8 rounded-full border-2 border-white/20 border-t-white animate-spin" />
      </div>
    );
  }

  if (!character) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center h-screen bg-zinc-950 text-white">
        <h2 className="text-2xl font-bold mb-4">Character Not Found</h2>
        <button onClick={onBack} className="px-6 py-3 rounded-lg bg-white/10 hover:bg-white/20 transition-all">
          Go Back
        </button>
      </div>
    );
  }

  const isComplete = !!character.is_finalized;
  const hasBody = !!character.core_layers.body;
  const hasHead = !!character.core_layers.head;
  const currentImage = galleryImages[activeImageIndex] || galleryImages[0];

  const viewerImages: ViewerImage[] = galleryImages.map(img => ({
    url: img.url,
    label: img.label
  }));

  // Masonry breakpoints for Images tab
  const masonryBreakpoints = {
    default: 4,
    1400: 3,
    1100: 3,
    768: 2,
    500: 2,
  };

  return (
    <div className="flex flex-col h-screen bg-onyx text-white overflow-hidden relative">
      {/* Character Production Bar */}
      <CharacterProductionBar
        characterName={character.name || 'Unnamed Character'}
        activeTab={activeTab as CharacterTab}
        onTabChange={(tab) => setActiveTab(tab as ProfileTab)}
        onBack={onBack}
      >
        {/* Action buttons in top bar */}
        {isComplete && passport && <button onClick={() => navigate(`/outfit?characterId=${encodeURIComponent(character.id)}&projectId=${encodeURIComponent(character.project_id)}`)} className="h-6 px-2.5 rounded-md bg-slate-blue hover:bg-[#8886F9] text-xs font-medium flex items-center gap-1.5"><SparkleIcon className="w-3 h-3"/>Wardrobe</button>}
        {!isComplete && onResumeSession && (
          <button
            onClick={() => onResumeSession(characterId)}
            className="h-6 px-2.5 rounded-md bg-slate-blue hover:bg-[#8886F9] transition-colors text-xs font-medium flex items-center gap-1.5"
          >
            <PlayCircleIcon className="w-3 h-3" />
            {character?.builder_session?.phase === 'dual-viewport'
              ? 'Resume Generation'
              : character?.builder_session?.phase === 'dual-review'
                ? 'Resume Review'
                : 'Continue Creating'}
          </button>
        )}
        {hasBody && hasHead && !passport && !isGeneratingPassport && (
          <button
            onClick={handleGeneratePassport}
            className="h-6 px-2.5 rounded-md bg-slate-blue hover:bg-[#8583f9] transition-colors text-xs font-medium flex items-center gap-1.5"
          >
            <SparkleIcon className="w-3 h-3" />
            Generate Reference Sheet
            <CreditCostBadge cost={SPECIAL_CREDIT_COSTS.passportGeneration} />
          </button>
        )}
        {isGeneratingPassport && (
          <div className="h-6 px-2.5 rounded-md bg-white/10 text-xs font-medium flex items-center gap-1.5">
            <CircleNotchIcon className="w-3 h-3 animate-spin" />
            Generating...
          </div>
        )}
      </CharacterProductionBar>

      {/* Main Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-5xl mx-auto px-8 py-8 pb-24">
          {/* Voice Tab - Full width layout */}
          {activeTab === 'voice' && (
            <div className="flex gap-6 items-start">
              {/* Left Column: Saved Voices */}
              {user && (
                <div className="shrink-0 w-48">
                  <SavedVoicesPanel
                    characterId={characterId}
                    userId={user.id}
                    selectedSampleId={selectedSavedSample?.id}
                    onSelectSample={(sample) => setSelectedSavedSample(sample)}
                    onCreateNew={() => setSelectedSavedSample(null)}
                    refreshTrigger={savedSamplesRefreshTrigger}
                    className="w-full"
                  />
                </div>
              )}

              {/* Right Column: Voice Configurator */}
              <div className="flex-1 space-y-4">
                {user && voiceConfigState && (
                  <>
                    <VoiceHeaderSection
                      state={voiceConfigState}
                      className="bg-carbon border border-graphite rounded-2xl p-5"
                    />
                    <VoiceSettingsSection
                      state={voiceConfigState}
                      headImageUrl={character.core_layers.head?.rendered_url}
                      bodyImageUrl={character.core_layers.body?.rendered_url}
                      referenceSheetUrl={passport?.reference_sheet?.front_url}
                      archetype={character.archetype}
                      characterName={character.name}
                      characterDescription={character.description}
                    />
                  </>
                )}
              </div>
            </div>
          )}

          {activeTab === 'biography' && (
            <div className="space-y-8">
              {/* Description Text */}
              {passport?.text_description && (
                <div className="p-6 rounded-2xl bg-carbon border border-graphite">
                  <div className="flex items-center gap-2 mb-4">
                    <ArticleIcon className="w-5 h-5 text-slate-blue" />
                    <h2 className="text-lg font-semibold text-dust">Character Description</h2>
                  </div>
                  <p className="text-base text-white/70 leading-relaxed">
                    {passport.text_description}
                  </p>
                </div>
              )}

              {/* Core Desire & Main Flaw */}
              <div className="grid grid-cols-2 gap-6">
                {/* Core Desire */}
                <div className="p-6 rounded-2xl bg-carbon border border-graphite">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <HeartIcon className="w-5 h-5 text-[#69F0AE]" weight="fill" />
                      <span className="text-lg font-semibold text-dust">Core Desire</span>
                    </div>
                    {!isEditingDesire && canEdit && (
                      <button
                        onClick={() => setIsEditingDesire(true)}
                        className="p-2 rounded-lg hover:bg-graphite transition-colors"
                      >
                        <PencilSimpleIcon className="w-4 h-4 text-iron" />
                      </button>
                    )}
                  </div>
                  {isEditingDesire ? (
                    <div className="space-y-3">
                      <textarea
                        value={coreDesire}
                        onChange={(e) => setCoreDesire(e.target.value)}
                        placeholder="What drives this character?"
                        className="w-full h-32 bg-onyx border border-graphite rounded-lg p-3 text-base text-white focus:outline-none focus:border-[#69F0AE]/50 resize-none placeholder:text-iron"
                        autoFocus
                      />
                      <div className="flex gap-2">
                        <button
                          onClick={handleSaveCoreDesire}
                          disabled={isSaving}
                          className="px-4 py-2 rounded-lg bg-[#69F0AE] hover:bg-[#7cf1b8] text-onyx text-sm font-medium transition-colors disabled:opacity-50"
                        >
                          Save
                        </button>
                        <button
                          onClick={() => {
                            setIsEditingDesire(false);
                            setCoreDesire((character as any).core_desire || '');
                          }}
                          className="px-4 py-2 text-white/50 text-sm hover:text-white/70 transition-colors"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <p className="text-base text-white/60 leading-relaxed">
                      {coreDesire || 'Click edit to define their deepest motivation.'}
                    </p>
                  )}
                </div>

                {/* Main Flaw */}
                <div className="p-6 rounded-2xl bg-carbon border border-graphite">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <WarningIcon className="w-5 h-5 text-[#FFD740]" weight="fill" />
                      <span className="text-lg font-semibold text-dust">Main Flaw</span>
                    </div>
                    {!isEditingFlaw && canEdit && (
                      <button
                        onClick={() => setIsEditingFlaw(true)}
                        className="p-2 rounded-lg hover:bg-graphite transition-colors"
                      >
                        <PencilSimpleIcon className="w-4 h-4 text-iron" />
                      </button>
                    )}
                  </div>
                  {isEditingFlaw ? (
                    <div className="space-y-3">
                      <textarea
                        value={mainFlaw}
                        onChange={(e) => setMainFlaw(e.target.value)}
                        placeholder="What holds this character back?"
                        className="w-full h-32 bg-onyx border border-graphite rounded-lg p-3 text-base text-white focus:outline-none focus:border-[#FFD740]/50 resize-none placeholder:text-iron"
                        autoFocus
                      />
                      <div className="flex gap-2">
                        <button
                          onClick={handleSaveMainFlaw}
                          disabled={isSaving}
                          className="px-4 py-2 rounded-lg bg-[#FFD740] hover:bg-[#ffdd5a] text-onyx text-sm font-medium transition-colors disabled:opacity-50"
                        >
                          Save
                        </button>
                        <button
                          onClick={() => {
                            setIsEditingFlaw(false);
                            setMainFlaw((character as any).main_flaw || '');
                          }}
                          className="px-4 py-2 text-iron text-sm hover:text-dust transition-colors"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <p className="text-base text-white/60 leading-relaxed">
                      {mainFlaw || 'Click edit to define their greatest weakness.'}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}


          {activeTab === 'reference' && (
            <div className="space-y-8">
              {/* Gallery Section */}
              {galleryImages.length > 0 ? (
                <div>
                  <div className="flex items-center gap-3 mb-6">
                    <ImagesIcon className="w-5 h-5 text-slate-blue" />
                    <h2 className="text-lg font-semibold text-dust">Visual Reference</h2>
                    {galleryImages.length > 1 && (
                      <div className="flex items-center gap-1 ml-auto">
                        <button
                          onClick={() => setActiveImageIndex((prev) => (prev - 1 + galleryImages.length) % galleryImages.length)}
                          className="w-8 h-8 rounded-lg bg-carbon hover:bg-graphite flex items-center justify-center transition-colors"
                        >
                          <CaretLeftIcon className="w-4 h-4 text-dust" />
                        </button>
                        <span className="text-sm text-iron px-2">
                          {activeImageIndex + 1} / {galleryImages.length}
                        </span>
                        <button
                          onClick={() => setActiveImageIndex((prev) => (prev + 1) % galleryImages.length)}
                          className="w-8 h-8 rounded-lg bg-carbon hover:bg-graphite flex items-center justify-center transition-colors"
                        >
                          <CaretRightIcon className="w-4 h-4 text-dust" />
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Main Image */}
                  <div className="relative rounded-2xl overflow-hidden bg-carbon border border-graphite group">
                    {currentImage && (
                      <img
                        src={currentImage.webUrl}
                        alt={currentImage.label}
                        className="w-full max-h-[600px] object-contain cursor-pointer"
                        onClick={() => setFullscreenImageIndex(activeImageIndex)}
                      />
                    )}
                    <div className="absolute top-4 left-4 px-3 py-1.5 rounded-lg bg-carbon/80 backdrop-blur-sm text-xs font-medium text-dust">
                      {currentImage?.label}
                    </div>
                    <div className="absolute bottom-4 right-4 px-3 py-1.5 rounded-lg bg-carbon/80 backdrop-blur-sm text-xs font-medium text-iron opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1.5">
                      <ArrowsOutIcon className="w-3.5 h-3.5" />
                      Click to expand
                    </div>
                  </div>

                  {/* Thumbnail Strip */}
                  {galleryImages.length > 1 && (
                    <div className="flex gap-3 mt-4">
                      {galleryImages.map((img, idx) => (
                        <button
                          key={img.type}
                          onClick={() => setActiveImageIndex(idx)}
                          className={`relative rounded-xl overflow-hidden border-2 transition-all ${idx === activeImageIndex
                            ? 'border-slate-blue ring-2 ring-slate-blue/20'
                            : 'border-graphite hover:border-iron opacity-60 hover:opacity-100'
                            }`}
                          style={{
                            width: img.type === 'reference' ? '120px' : '80px',
                            aspectRatio: img.type === 'reference' ? '3/2' : '4/5'
                          }}
                        >
                          <img src={img.webUrl} alt={img.label} className="w-full h-full object-cover" />
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <div className="p-12 rounded-2xl bg-carbon border border-graphite text-center">
                  <ImagesIcon className="w-12 h-12 text-iron mx-auto mb-4" />
                  <p className="text-lg text-dust/60 mb-2">No visual reference yet</p>
                  <p className="text-sm text-iron">Generate a reference sheet to see visual assets here.</p>
                </div>
              )}

            </div>
          )}

          {/* Images Tab - Generated images using this character */}
          {activeTab === 'images' && (
            <div className="space-y-6">
              <div className="flex items-center gap-3">
                <ImageSquareIcon className="w-5 h-5 text-slate-blue" />
                <h2 className="text-lg font-semibold text-dust">Generated Images</h2>
                <span className="text-sm text-white/40">
                  {characterImages.length} image{characterImages.length !== 1 ? 's' : ''}
                </span>
              </div>

              {isLoadingImages ? (
                <div className="flex items-center justify-center py-20">
                  <CircleNotchIcon className="w-8 h-8 text-slate-blue animate-spin" />
                </div>
              ) : characterImages.length > 0 ? (
                <Masonry
                  breakpointCols={masonryBreakpoints}
                  className="flex w-auto -ml-4"
                  columnClassName="pl-4 bg-clip-padding"
                >
                  {characterImages.map((gen, index) => (
                    <div
                      key={gen.id}
                      className="mb-4 group relative rounded-xl overflow-hidden bg-carbon border border-graphite hover:border-iron cursor-pointer transition-all"
                      onClick={() => setImagesFullscreenIndex(index)}
                    >
                      <img
                        src={gen.image_url || gen.img || ''}
                        alt={gen.prompt || 'Generated image'}
                        className="w-full h-auto"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                        <div className="absolute bottom-0 left-0 right-0 p-3">
                          <p className="text-xs text-white/80 line-clamp-2">
                            {gen.prompt || 'No prompt'}
                          </p>
                          <p className="text-[10px] text-white/50 mt-1">
                            {gen.model || 'Unknown model'}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </Masonry>
              ) : (
                <div className="p-12 rounded-2xl bg-carbon border border-graphite text-center">
                  <ImageSquareIcon className="w-12 h-12 text-iron mx-auto mb-4" />
                  <p className="text-lg text-dust/60 mb-2">No generated images yet</p>
                  <p className="text-sm text-iron">
                    Images created using this character as a reference will appear here.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Bottom Tab Bar - Pill with avatar overlay */}
      <div
        className="fixed bottom-8 z-50 transition-all duration-300"
        style={{
          left: `calc(50% + ${(() => {
            const NAV_WIDTH = 240;
            const PANEL_WIDTH = 640;
            const leftWidth = sidebarState.navPosition === 'left'
              ? (sidebarState.isSidebarOpen ? NAV_WIDTH : 0)
              : (sidebarState.isRightPanelOpen ? PANEL_WIDTH : 0);
            const rightWidth = sidebarState.navPosition === 'left'
              ? (sidebarState.isRightPanelOpen ? PANEL_WIDTH : 0)
              : (sidebarState.isSidebarOpen ? NAV_WIDTH : 0);
            return (leftWidth - rightWidth) / 2;
          })()}px)`,
          transform: 'translateX(-50%)'
        }}
      >
        <div className="relative flex items-center">
          {/* Pill Bar - two-row layout with original sizes */}
          <div className="bg-zinc-900 rounded-[100px] pl-[220px] pr-8 h-[200px] border border-white/10 flex flex-col justify-center gap-3 shadow-2xl shadow-black/50">
            {/* Character Info */}
            <div className="flex flex-col">
              {isEditingName ? (
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={editingName}
                    onChange={(e) => setEditingName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleSaveCharacterName();
                      if (e.key === 'Escape') handleCancelEditName();
                    }}
                    onBlur={handleSaveCharacterName}
                    autoFocus
                    className="text-xl font-semibold text-white leading-tight max-w-[280px] bg-onyx px-2 py-1 rounded border border-white/20 focus:outline-none focus:border-indigo-400"
                  />
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <span className="text-xl font-semibold text-white leading-tight max-w-[280px] truncate">
                    {character.name || 'Unnamed'}
                  </span>
                  {canEdit && (
                    <button
                      onClick={handleStartEditName}
                      className="p-1 rounded hover:bg-white/10 transition-colors"
                      title="Edit name"
                    >
                      <Pencil className="w-4 h-4 text-zinc-400 hover:text-white" weight="regular" />
                    </button>
                  )}
                </div>
              )}
              {(character.archetype || character.style_preset) && (
                <span className="text-sm text-white/50 truncate max-w-[280px]">
                  {[
                    formatArchetype(character.archetype),
                    character.archetype && character.style_preset
                      ? getStyleById(character.archetype, character.style_preset)?.name || character.style_preset
                      : null
                  ].filter(Boolean).join(' · ')}
                </span>
              )}
            </div>

            {/* Tab Buttons - row below name */}
            <div className="flex items-center gap-2">
              {CHARACTER_TABS.map(tab => {
                const isActive = tab === activeTab;
                const TabIcon = TAB_CONFIG[tab].icon;
                return (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab as ProfileTab)}
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-full transition-all ${
                      isActive
                        ? 'bg-white/10 text-white'
                        : 'text-white/40 hover:text-white/70 hover:bg-white/5'
                    }`}
                  >
                    <TabIcon weight={isActive ? 'fill' : 'regular'} className="w-5 h-5" />
                    <span className="text-sm font-medium">{TAB_CONFIG[tab].label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Avatar - overlays the left edge of pill */}
          <div
            className="absolute left-0 top-0 z-10 group cursor-pointer"
            onMouseDown={(e) => {
              if (!hasHead || !canEdit) return;
              e.preventDefault();
              setIsAdjustingAvatar(true);
              const startY = e.clientY;
              const startPos = avatarYPosition;
              let finalPos = startPos;

              const handleMove = (moveE: MouseEvent) => {
                const delta = (moveE.clientY - startY) * 0.2;
                finalPos = Math.max(0, Math.min(100, startPos + delta));
                setAvatarYPosition(finalPos);
              };

              const handleUp = async () => {
                document.removeEventListener('mousemove', handleMove);
                document.removeEventListener('mouseup', handleUp);
                if (finalPos !== startPos && character) {
                  console.log('Saving avatar position:', finalPos);
                  const success = await updateAvatarYPosition(character.id, Math.round(finalPos));
                  if (success) {
                    setCharacter({ ...character, avatar_y_position: Math.round(finalPos) } as any);
                    showToast('Avatar position saved', 'success');
                  } else {
                    showToast('Failed to save avatar position', 'error');
                  }
                }
                setIsAdjustingAvatar(false);
              };

              document.addEventListener('mousemove', handleMove);
              document.addEventListener('mouseup', handleUp);
            }}
          >
            <div className={`w-[200px] h-[200px] rounded-full overflow-hidden border-4 border-zinc-900 shadow-2xl shadow-black/60 transition-all ${isAdjustingAvatar ? 'ring-4 ring-slate-blue border-slate-blue' : 'hover:border-neutral-700'}`}>
              {hasHead ? (
                <img
                  src={character.core_layers.head!.rendered_url}
                  alt={character.name}
                  className="w-full h-full object-cover select-none"
                  style={{ objectPosition: `center ${avatarYPosition}%` }}
                  draggable={false}
                />
              ) : (
                <div className="w-full h-full bg-graphite flex items-center justify-center">
                  <UserIcon className="w-16 h-16 text-white/30" />
                </div>
              )}
            </div>
            {hasHead && !isAdjustingAvatar && (
              <div className="absolute inset-0 rounded-full bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center pointer-events-none">
                <ArrowsOutCardinalIcon className="w-8 h-8 text-white/80" />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Fullscreen Image Viewer (Reference tab) */}
      <FullscreenImageViewer
        images={viewerImages}
        currentIndex={fullscreenImageIndex}
        onClose={() => setFullscreenImageIndex(null)}
        onNavigate={setFullscreenImageIndex}
        fixed={false}
      />

      {/* Fullscreen Image Viewer (Images tab) */}
      <FullscreenImageViewer
        images={characterViewerImages}
        currentIndex={imagesFullscreenIndex}
        onClose={() => setImagesFullscreenIndex(null)}
        onNavigate={setImagesFullscreenIndex}
        fixed={false}
      />
    </div>
  );
};
